import PropTypes from 'prop-types';
import React from 'react';
import { pure, isNative } from '@dbk/ui-react-utils';

import { Svg, Path, G, Defs, ClipPath, Text } from '@dbk/ui-svg';
import TSpan from '../../utils/tspan';
import createChartData from './donut-chart-data';
import { polarToCartesian, DEG_RADS } from './geometry';
import {
  LINE_WIDTH_PERCENT,
  BASE_ROTATION,
  SHADOW_SIZE_PERCENT,
  SEPARATOR_WIDTH,
  MAX_WIDTH_SCREEN
} from './donut-chart-constants';

import ResponsiveDimensionValues from "../../../components/common/responsive-dimension-values";

const CHART_PADDING = 32;
const INNER_PADDING = 20;
const MOBILE_LIMIT = 641;
const isMobile = ResponsiveDimensionValues.windowWidth < MOBILE_LIMIT;
const SELECTION_OFFSET = isNative ?  20 : isMobile ? 20 : 10;

const DonutChartSvg = ({ items, size, onSelectedItem, formatValue, isTablet }) => {

  var WINDOW_WIDTH = isTablet ? size + CHART_PADDING * 2 + 25 || ResponsiveDimensionValues.windowWidth : ResponsiveDimensionValues.windowWidth;
  let CHART_WIDTH = size;



  const CHART_SIZE = CHART_WIDTH - CHART_PADDING * 2 - 25;
  const CHART_RADIUS = CHART_SIZE / 2;
  let LINE_WIDTH = CHART_WIDTH <= 310 ? 0.25 * CHART_SIZE : LINE_WIDTH_PERCENT * CHART_SIZE;
  const paddingSize = CHART_WIDTH <= 310 ? 0 : LINE_WIDTH;
  const SHADOW_SIZE = SHADOW_SIZE_PERCENT * CHART_SIZE;
  // prettier-ignore
  const VIEW_BOX = CHART_WIDTH;
  const X = CHART_WIDTH / 2;
  const Y = X;

  let FONT_SIZE = isNative ? 16 : isMobile ? 20: 15;

  if(CHART_WIDTH <= 310){
    FONT_SIZE = 12;
  }


  function getArc(fromAngle, toAngle, offset) {
    // prettier-ignore
    const arcRadius = CHART_RADIUS - ((paddingSize - offset) / 2);
    const startPoint = polarToCartesian(fromAngle, arcRadius);
    const endPoint = polarToCartesian(toAngle, arcRadius);
    const diff = toAngle - fromAngle;
    const isLarge = diff > Math.PI;

    if (isLarge) {
      // prettier-ignore
      const firstArc = getArc(fromAngle, toAngle - (diff / 2), offset);
      // prettier-ignore
      const secondArc = getArc(fromAngle + (diff / 2), toAngle, offset);
      return `${firstArc} ${secondArc}`;
    }

    return [
      'M',
      startPoint.x,
      startPoint.y,
      'A',
      arcRadius,
      arcRadius,
      '0 0 1',
      endPoint.x,
      endPoint.y,
    ].join(' ');
  }

  function getClipPaths(items, clipPathId) {
    const full = VIEW_BOX;
    const half = VIEW_BOX / 2;
    const outerRectPath = `M-${half} -${half}h${full}v${full}h-${full}v-${full}`;
    const separatorPath = [
      `M-${0.5 * SEPARATOR_WIDTH} -${half}`,
      `v${half}`,
      `h${SEPARATOR_WIDTH}`,
      `v-${half}`,
      `h-${SEPARATOR_WIDTH}`,
    ].join('');

    return (
      <ClipPath id={clipPathId}>
        <Path d={outerRectPath} />
        {items.map(({ fromAngle, id }) => {
          const segmentAngle = fromAngle / DEG_RADS;
          return (
            <Path
              d={separatorPath}
              key={id}
              transform={getTransform(segmentAngle)}
            />
          );
        })}
      </ClipPath>
    );
  }

  function getTransform(segmentAngle) {
    if (isNative) {
      return [{ rotate: segmentAngle }];
    }
    return `rotate(${segmentAngle})`;
  }

  let clipPathIdCounter = 0;
  const itemsToUse = createChartData(items);
  const needsClipPath = items && items.length > 1;
  const clipPathId = `separatorPaths${clipPathIdCounter++}`;
  const clipPath = needsClipPath ? `url(#${clipPathId})` : undefined;
  return (
    <Svg viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`} width={CHART_WIDTH} height={CHART_WIDTH}>
      {needsClipPath && <Defs>{getClipPaths(items, clipPathId)}</Defs>}
      <G x={X} y={Y} key={size} clipPath={clipPath} >
        {itemsToUse.map((item, index) => {
          const { fromAngle, toAngle, color, selected, value } = item;
          if (fromAngle === toAngle) {
            return null;
          }
          const pathId = `paths${index}`;
          let formattedValue = value;

          if (formatValue) {
            formattedValue = formatValue(item);
          }

          if (!selected) {
            return (
              <Path
                key={color}
                id={pathId}
                d={getArc(
                  fromAngle + BASE_ROTATION,
                  toAngle + BASE_ROTATION + 0.005,
                  selected ? SELECTION_OFFSET : 0,
                )}
                stroke={color}
                strokeWidth={selected ? LINE_WIDTH + SELECTION_OFFSET : LINE_WIDTH}
                fill="none"
              />);
          }

          const toAngleWithBaseRotation = BASE_ROTATION + toAngle;
          const fromAngleWithBaseRotation = BASE_ROTATION + fromAngle;

          const normalEnd = toAngleWithBaseRotation < fromAngleWithBaseRotation ? toAngleWithBaseRotation + 2 * Math.PI : toAngleWithBaseRotation;

          const centerAngle = fromAngleWithBaseRotation + (normalEnd - fromAngleWithBaseRotation) / 2 + 0.005;

          const arcRadius = CHART_RADIUS - ((paddingSize - (selected ? SELECTION_OFFSET : 0)) / 2);

          const centerPoint = polarToCartesian(centerAngle, arcRadius);

          return (
            [
              <Path
                key={color}
                id={pathId}
                d={getArc(
                  fromAngle + BASE_ROTATION,
                  toAngle + BASE_ROTATION + 0.005,
                  selected ? SELECTION_OFFSET : 0,
                )}
                stroke={color}
                strokeWidth={selected ? LINE_WIDTH + SELECTION_OFFSET : LINE_WIDTH}
                fill="none"
              />,
              <Text
                key={`text${color}`}
                x={0}
                y={0}
                style={{ position: 'absolute' }}
                transform={`translate(${centerPoint.x}, ${centerPoint.y})`}
                textAnchor="middle">
                <TSpan fill="#000E28"
                       textAnchor="middle"
                       alignmentBaseline="middle"
                       fontWeight="bold"
                       fontSize={FONT_SIZE}>
                  {formattedValue}
                </TSpan>
                {/*<TextPath startOffset="50%" textAnchor="middle" href={`#${pathId}`}>*/}
                {/*{formattedValue}*/}
                {/*</TextPath>*/}
              </Text>
            ]);
        })}
      </G>
    </Svg>
  );
}

DonutChartSvg.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      selected: PropTypes.bool,
      fromAngle: PropTypes.number,
      toAngle: PropTypes.number,
    })
  ),
  size: PropTypes.number,
  onSelectedItem: PropTypes.func,
  formatValue: PropTypes.func,
  selectedIndex: PropTypes.number,
  isTablet: PropTypes.bool,
};

DonutChartSvg.defaultProps = {
  items: [],
  size: undefined,
  isTablet: false,
};

export default pure(DonutChartSvg);
